import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eligibleExperiencesQueryOptions, GUIDED_EXPERIENCE_QUERY_KEYS } from '@/api/queries/guided-experiences';
import * as guidedExperiencesApi from '@/api/endpoints/guided-experiences';
import { useAuth } from '@/hooks/use-auth';
import type {
  OnboardingIntent,
  OnboardingPayload,
  OnboardingStep,
  OnboardingStatus,
  UseOnboardingReturn,
} from '../types';

const EXPERIENCE_ID = 'onboarding';

export function useOnboarding(): UseOnboardingReturn {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id ?? '';

  const { data: eligibilityData, isLoading, isError } = useQuery(
    eligibleExperiencesQueryOptions(userId)
  );

  const onboardingExperience = useMemo(
    () => eligibilityData?.experiences.find((e) => e.experience_id === EXPERIENCE_ID),
    [eligibilityData]
  );

  const serverStep = onboardingExperience?.current_step;

  const [localStep, setLocalStep] = useState<OnboardingStep | null>(null);
  const [payload, setPayload] = useState<OnboardingPayload>({});
  const [isAdvancing, setIsAdvancing] = useState(false);

  const currentStep: OnboardingStep = (localStep ?? serverStep ?? 1) as OnboardingStep;

  const status: OnboardingStatus = useMemo(() => {
    if (isLoading) return 'loading';
    if (isError) return 'error';
    if (!onboardingExperience) return 'not_required';
    return 'required';
  }, [isLoading, isError, onboardingExperience]);

  const start = useCallback(async () => {
    setIsAdvancing(true);
    try {
      const state = await guidedExperiencesApi.startExperience(EXPERIENCE_ID);
      const step = (state.current_step ?? 1) as OnboardingStep;
      setLocalStep(step);
      if (state.payload) {
        try {
          setPayload(JSON.parse(state.payload));
        } catch {
          // payload parse failure is non-fatal
        }
      }
    } finally {
      setIsAdvancing(false);
    }
  }, []);

  const nextStep = useCallback(async () => {
    const next = (currentStep + 1) as OnboardingStep;
    setIsAdvancing(true);
    try {
      await guidedExperiencesApi.updateExperienceState(EXPERIENCE_ID, {
        current_step: next,
        payload: JSON.stringify(payload),
      });
      setLocalStep(next);
    } finally {
      setIsAdvancing(false);
    }
  }, [currentStep, payload]);

  const goBack = useCallback(() => {
    if (currentStep > 1) {
      setLocalStep((currentStep - 1) as OnboardingStep);
    }
  }, [currentStep]);

  const setIntent = useCallback((intent: OnboardingIntent) => {
    setPayload((prev) => ({ ...prev, intent }));
  }, []);

  const skipFriendSeeding = useCallback(() => {
    setPayload((prev) => ({ ...prev, friend_seeding_skipped: true }));
  }, []);

  const complete = useCallback(async () => {
    setIsAdvancing(true);
    try {
      await guidedExperiencesApi.updateExperienceState(EXPERIENCE_ID, {
        current_step: 6,
        payload: JSON.stringify(payload),
      });
      await guidedExperiencesApi.completeExperience(EXPERIENCE_ID);
      queryClient.invalidateQueries({
        queryKey: GUIDED_EXPERIENCE_QUERY_KEYS.eligible(userId),
      });
    } finally {
      setIsAdvancing(false);
    }
  }, [payload, queryClient, userId]);

  return {
    status,
    currentStep,
    payload,
    isAdvancing,
    start,
    nextStep,
    goBack,
    setIntent,
    skipFriendSeeding,
    complete,
  };
}
